/**
 * CheckoutPage (Подтверждение заказа) — the cart's order-confirmation screen (ТЗ §5, §10).
 *
 * The cart's «Оформить заказ» lands here instead of creating an order directly. The screen
 * lists the selected lines, the available ЗБ / primogems with spend fields (the primogem cap
 * is the server-summed per-product limit), and the final amount to pay. **Every amount comes
 * from ``POST /api/cart/quote/``** — the client never sums or caps anything itself; it only
 * sends intent (ids, quantities, requested spend). «Подтвердить заказ» creates the order
 * (``POST /api/orders/``) and lands the user in the order chat.
 *
 * Double-submit guard (ТЗ §10): the button is disabled until the server responds and the
 * idempotency key is stable per cart composition (regenerated only when the selection changes,
 * never when the spend changes), so a retry after a lost response returns the already-created
 * order rather than a second one. The spend fields are also locked while a submit is in flight
 * so the in-flight request and its idempotency key stay consistent.
 */
import { useRef, useState } from "react";

import { Price } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  cartLines,
  cartCount,
  useCart,
  useCartQuote,
  useCheckout,
} from "@features/cart";
import { parseSpend } from "@features/product-order";
import { useMe } from "@entities/user";
import { StatePanel } from "@widgets/catalog-browser";
import { ShopClosedBanner } from "@widgets/shop-status";
import { ApiError } from "@shared/api";
import { formatRub } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import styles from "./CheckoutPage.module.css";

function apiErrorText(err: ApiError, fallback: string): string {
  const payload = err.payload as { readonly detail?: unknown } | null;
  if (payload !== null && typeof payload.detail === "string") {
    return payload.detail;
  }
  return fallback;
}

function itemsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "товар";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "товара";
  }
  return "товаров";
}

export function CheckoutPage(): JSX.Element {
  const navigate = useNavigate();
  const cart = useCart();
  const { data: me } = useMe();

  const [glitterInput, setGlitterInput] = useState("");
  const [primogemInput, setPrimogemInput] = useState("");
  const [comment, setComment] = useState("");
  // «Нужен чек» — off by default (ТЗ №2доп §16-18); sent verbatim to order creation.
  const [needsReceipt, setNeedsReceipt] = useState(false);

  const glitterSpend = parseSpend(glitterInput);
  const primogemSpend = parseSpend(primogemInput);

  const quoteState = useCartQuote(cart.items, {
    starGlitterSpend: glitterSpend,
    primogemSpend,
  });
  const checkout = useCheckout(cart.items, {
    starGlitterSpend: glitterSpend,
    primogemSpend,
    userComment: comment,
    needsReceipt,
  });
  const submittedRef = useRef(false);

  const lines = cartLines(cart.items);
  const firstGame = lines[0]?.product.game;

  const goBack = (): void => {
    navigate(firstGame !== undefined ? routes.shopGame(firstGame) : routes.shop());
  };
  useBackButton(goBack);

  // Empty cart (e.g. deep-link or after a completed checkout) — nothing to confirm.
  if (cart.items.size === 0) {
    return (
      <div className={styles.page}>
        <Header onBack={goBack} subline="" />
        <div className={styles.scroll}>
          <StatePanel kind="empty" message="Корзина пуста" />
        </div>
        <div className={styles.bottomBar}>
          <button
            type="button"
            className={styles.confirm}
            onClick={() => navigate(routes.shop())}
          >
            В магазин
          </button>
        </div>
      </div>
    );
  }

  const { quote } = quoteState;
  const quoteHasError = quoteState.error !== null;
  const anyGlitterAllowed = lines.some((line) => line.product.star_glitter_allowed);

  // The total to pay — straight from the server quote; falls back to "…" while first pricing.
  const total = quote?.total ?? null;
  const unitCount = cartCount(cart.items);

  const canConfirm =
    quote !== null && !quoteHasError && !quoteState.isLoading && !checkout.isSubmitting;

  const confirm = (): void => {
    if (submittedRef.current || !canConfirm) {
      return;
    }
    submittedRef.current = true;
    checkout
      .submit()
      .then((order) => {
        cart.clear();
        navigate(routes.order(order.id));
      })
      .catch(() => {
        // Allow a retry — the stable idempotency key keeps it safe server-side.
        submittedRef.current = false;
      });
  };

  const confirmError =
    checkout.error !== null && !quoteHasError
      ? apiErrorText(checkout.error, "Не удалось оформить заказ.")
      : null;

  return (
    <div className={styles.page}>
      <Header
        onBack={goBack}
        subline={`${unitCount} ${itemsWord(unitCount)}`}
      />

      <div className={styles.scroll}>
        {/* Soft notice while outside working hours (ТЗ №7) — ordering still allowed. */}
        {me ? <ShopClosedBanner me={me} /> : null}

        {/* ── lines ─────────────────────────────────────────────── */}
        <section className={styles.card} aria-label="Состав заказа">
          <ul className={styles.items}>
            {lines.map((line) => {
              const quoteLine = quote?.lines.find(
                (l) => l.product_id === line.product.id,
              );
              return (
                <li key={line.product.id} className={styles.item}>
                  <span className={styles.itemTitle}>
                    {line.product.title}
                    {line.qty > 1 ? ` ×${line.qty}` : ""}
                  </span>
                  {quoteLine !== undefined ? (
                    <Price amount={quoteLine.line_total} size="sm" />
                  ) : (
                    <span className={styles.itemMuted}>…</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── currency spend (server-priced) ────────────────────── */}
        <section className={styles.card} aria-label="Списание валют">
          <h2 className={styles.cardTitle}>Списать валюту</h2>
          {anyGlitterAllowed ? (
            <SpendField
              label="Звёздный блеск"
              glyph="✦"
              value={glitterInput}
              available={quote?.available_star_glitter}
              appliedRub={quote?.star_glitter_applied_rub}
              capRub={quote?.max_star_glitter_rub}
              onChange={setGlitterInput}
              disabled={checkout.isSubmitting}
            />
          ) : null}
          <SpendField
            label="Примогемы"
            glyph="◆"
            value={primogemInput}
            available={quote?.available_primogems}
            appliedRub={quote?.primogem_applied_rub}
            capRub={quote?.max_primogem_rub}
            onChange={setPrimogemInput}
            disabled={checkout.isSubmitting}
          />
          {quoteHasError ? (
            <p className={styles.spendError} role="alert">
              {apiErrorText(
                quoteState.error as ApiError,
                "Превышен лимит списания. Уменьшите сумму.",
              )}
            </p>
          ) : null}
        </section>

        {/* ── comment ───────────────────────────────────────────── */}
        <section className={styles.card} aria-label="Комментарий">
          <h2 className={styles.cardTitle}>Комментарий к заказу</h2>
          <textarea
            className={styles.comment}
            placeholder="Ник в игре, пожелания, детали…"
            value={comment}
            maxLength={2000}
            rows={3}
            onChange={(event) => setComment(event.target.value)}
          />
        </section>

        {/* ── receipt toggle (ТЗ №2доп §16-18) ──────────────────── */}
        <section className={styles.card} aria-label="Чек об оплате">
          <label className={styles.receiptRow}>
            <span className={styles.receiptInfo}>
              <span className={styles.receiptLabel}>Нужен чек</span>
              <span className={styles.receiptHint}>
                Продавец приложит чек об оплате к заказу.
              </span>
            </span>
            <span
              className={styles.toggle}
              data-on={needsReceipt ? "true" : "false"}
            >
              <input
                type="checkbox"
                className={styles.toggleInput}
                checked={needsReceipt}
                disabled={checkout.isSubmitting}
                onChange={(event) => setNeedsReceipt(event.target.checked)}
              />
              <span className={styles.toggleKnob} aria-hidden="true" />
            </span>
          </label>
        </section>

        {/* ── totals (server-computed) ──────────────────────────── */}
        <section className={styles.card} aria-label="Итоги">
          <div className={styles.totals}>
            <Row label="Сумма" value={quote?.subtotal ?? null} />
            {quote !== null && Number(quote.star_glitter_applied_rub) > 0 ? (
              <Row
                label="Звёздный блеск"
                value={`-${quote.star_glitter_applied_rub}`}
                discount
              />
            ) : null}
            {quote !== null && Number(quote.primogem_applied_rub) > 0 ? (
              <Row
                label="Примогемы"
                value={`-${quote.primogem_applied_rub}`}
                discount
              />
            ) : null}
            <div className={styles.grandRow}>
              <span className={styles.grandLabel}>К оплате</span>
              {total !== null ? (
                <Price amount={total} accent size="lg" />
              ) : (
                <span className={styles.itemMuted}>…</span>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className={styles.bottomBar}>
        {confirmError !== null ? (
          <p className={styles.orderError} role="alert">
            {confirmError}
          </p>
        ) : null}
        <button
          type="button"
          className={styles.confirm}
          disabled={!canConfirm}
          onClick={confirm}
        >
          {checkout.isSubmitting
            ? "Оформляем…"
            : total !== null
              ? `Подтвердить заказ · ${formatRub(total)}`
              : "Подтвердить заказ"}
        </button>
      </div>
    </div>
  );
}

interface HeaderProps {
  readonly onBack: () => void;
  readonly subline: string;
}

function Header({ onBack, subline }: HeaderProps): JSX.Element {
  return (
    <header className={styles.header}>
      <button
        type="button"
        className={styles.back}
        aria-label="Назад"
        onClick={onBack}
      >
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
      </button>
      <div className={styles.headMain}>
        <h1 className={styles.headTitle}>Подтверждение заказа</h1>
        {subline ? <span className={styles.subline}>{subline}</span> : null}
      </div>
    </header>
  );
}

interface RowProps {
  readonly label: string;
  readonly value: string | null;
  readonly discount?: boolean;
}

function Row({ label, value, discount = false }: RowProps): JSX.Element {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      {value !== null ? (
        <Price
          amount={value}
          size="sm"
          {...(discount ? { className: styles.discount } : {})}
        />
      ) : (
        <span className={styles.itemMuted}>…</span>
      )}
    </div>
  );
}

interface SpendFieldProps {
  readonly label: string;
  readonly glyph: string;
  readonly value: string;
  readonly available: number | undefined;
  readonly appliedRub: string | undefined;
  readonly capRub: string | undefined;
  readonly onChange: (next: string) => void;
  readonly disabled?: boolean;
}

function SpendField({
  label,
  glyph,
  value,
  available,
  appliedRub,
  capRub,
  onChange,
  disabled = false,
}: SpendFieldProps): JSX.Element {
  const applied = appliedRub !== undefined ? Number(appliedRub) : 0;
  const cap = capRub !== undefined ? Number(capRub) : NaN;
  return (
    <label className={styles.spendField}>
      <span className={styles.spendGlyph} aria-hidden="true">
        {glyph}
      </span>
      <span className={styles.spendInfo}>
        <span className={styles.spendLabel}>{label}</span>
        {available !== undefined ? (
          <span className={styles.spendBalance}>
            Доступно: {available.toLocaleString("ru-RU")}
            {Number.isFinite(cap) && cap > 0
              ? ` · до ${formatRub(cap)}`
              : ""}
          </span>
        ) : null}
      </span>
      <span className={styles.spendInputWrap}>
        <input
          className={styles.spendInput}
          type="number"
          inputMode="numeric"
          min={0}
          {...(available !== undefined ? { max: available } : {})}
          placeholder="0"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
        {applied > 0 ? (
          <span className={styles.spendApplied}>−{applied.toFixed(0)} ₽</span>
        ) : null}
      </span>
    </label>
  );
}
